import React, { useEffect, useState } from 'react';
import { Target, Eye, Award, Users, TrendingUp, Zap } from 'lucide-react';
import { AnimatedCounter } from '../components/AnimatedCounter';
import { TestimonialCarousel } from '../components/TestimonialCarousel';
export function About() {
  const testimonials = [{
    quote: 'Manuel Technologies transformed our vision into reality. Their expertise in digital transformation and commitment to excellence exceeded all expectations.',
    author: 'Adebayo Ogunlesi',
    role: 'Director of Digital Services',
    company: 'Edo State Government',
    image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=400&auto=format&fit=crop'
  }, {
    quote: "The AI solutions they developed have revolutionized our operations. We've seen a 300% increase in efficiency and significant cost savings.",
    author: 'Chiamaka Nwosu',
    role: 'Chief Technology Officer',
    company: 'FinServe Bank',
    image: 'https://images.unsplash.com/photo-1531123897727-8f129e1688ce?q=80&w=400&auto=format&fit=crop'
  }, {
    quote: 'Their business writing services helped us secure $2M in funding. The quality and strategic insight were exceptional.',
    author: 'Oluwaseun Adeyemi',
    role: 'CEO & Founder',
    company: 'TechHub Africa',
    image: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?q=80&w=400&auto=format&fit=crop'
  }];
  return <div className="min-h-screen bg-white pt-20" data-id="element-183">
      {/* Hero Section with Beautiful Header Image */}
      <section className="relative py-20 sm:py-32 bg-gradient-to-br from-slate-900 to-blue-900 text-white overflow-hidden" data-id="element-184">
        <div className="absolute inset-0 opacity-30" data-id="element-185">
          <img src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?q=80&w=2000&auto=format&fit=crop" alt="Diverse team collaborating on technology solutions" className="w-full h-full object-cover" data-id="element-186" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/80 to-blue-900/80" data-id="element-187" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6" data-id="element-188">
          <div className="max-w-3xl" data-id="element-189">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-black mb-6 animate-fade-in" data-id="element-190">
              About Manuel Technologies
            </h1>
            <p className="text-lg sm:text-xl text-slate-300 leading-relaxed" data-id="element-191">
              Since 2011, we've been at the forefront of digital transformation,
              delivering AI-powered solutions that drive business growth and
              innovation across Africa and beyond.
            </p>
          </div>
        </div>
      </section>

      {/* Company Overview with Image */}
      <section className="py-12 sm:py-20" data-id="element-192">
        <div className="max-w-7xl mx-auto px-4 sm:px-6" data-id="element-193">
          <div className="grid lg:grid-cols-2 gap-12 sm:gap-16 items-center" data-id="element-194">
            <div data-id="element-195">
              <h2 className="text-3xl sm:text-4xl font-black text-slate-900 mb-6" data-id="element-196">
                Who We Are
              </h2>
              <div className="space-y-4 text-base sm:text-lg text-slate-700 leading-relaxed" data-id="element-197">
                <p data-id="element-198">
                  <strong className="text-slate-900" data-id="element-199">
                    Manuel Technologies and Global Investments Limited
                  </strong>{' '}
                  (RC 997980) is a product-led technology company specializing
                  in AI-powered digital transformation solutions.
                </p>
                <p data-id="element-200">
                  Founded in 2011, we've grown from a small IT consultancy to a
                  leading provider of enterprise-grade software, cloud
                  infrastructure, and strategic technology services across
                  Nigeria and West Africa.
                </p>
                <p data-id="element-201">
                  Our expertise spans artificial intelligence, machine learning,
                  cybersecurity, data analytics, and custom software
                  development. We've successfully delivered over 130 projects,
                  including the landmark Edo State Government digital
                  transformation initiative.
                </p>
              </div>
            </div>
            <div className="relative" data-id="element-202">
              <div className="aspect-square rounded-2xl overflow-hidden shadow-2xl" data-id="element-203">
                <img src="https://images.unsplash.com/photo-1573164713714-d95e436ab8d6?q=80&w=2000&auto=format&fit=crop" alt="Professional Black business team in strategic meeting" className="w-full h-full object-cover hover:scale-110 transition-transform duration-700" data-id="element-204" />
              </div>
              <div className="absolute -bottom-6 -right-6 w-48 h-48 bg-blue-600 rounded-2xl -z-10" data-id="element-205" />
            </div>
          </div>
        </div>
      </section>

      {/* Animated Stats */}
      <section className="py-12 sm:py-20 bg-slate-50" data-id="element-206">
        <div className="max-w-7xl mx-auto px-4 sm:px-6" data-id="element-207">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8" data-id="element-208">
            <AnimatedStatCard value={12} suffix="+" label="Years in Business" color="blue" data-id="element-209" />
            <AnimatedStatCard value={130} suffix="+" label="Projects Delivered" color="cyan" data-id="element-210" />
            <AnimatedStatCard value={95} suffix="%" label="Client Satisfaction" color="indigo" data-id="element-211" />
            <AnimatedStatCard value={30} suffix="+" label="Expert Team Members" color="purple" data-id="element-212" />
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-12 sm:py-20" data-id="element-213">
        <div className="max-w-7xl mx-auto px-4 sm:px-6" data-id="element-214">
          <div className="grid md:grid-cols-2 gap-8 sm:gap-12" data-id="element-215">
            <div className="group p-8 sm:p-10 bg-gradient-to-br from-blue-50 to-white rounded-2xl shadow-lg border-t-4 border-blue-600 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2" data-id="element-216">
              <div className="w-14 h-14 sm:w-16 sm:h-16 bg-blue-100 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform" data-id="element-217">
                <Target className="w-7 h-7 sm:w-8 sm:h-8 text-blue-600" data-id="element-218" />
              </div>
              <h3 className="text-2xl sm:text-3xl font-black text-slate-900 mb-4" data-id="element-219">
                Our Mission
              </h3>
              <p className="text-base sm:text-lg text-slate-700 leading-relaxed" data-id="element-220">
                To empower organizations through innovative, AI-driven
                technology solutions that transform operations, enhance
                efficiency, and drive sustainable growth in the digital economy.
              </p>
            </div>

            <div className="group p-8 sm:p-10 bg-gradient-to-br from-cyan-50 to-white rounded-2xl shadow-lg border-t-4 border-cyan-600 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2" data-id="element-221">
              <div className="w-14 h-14 sm:w-16 sm:h-16 bg-cyan-100 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform" data-id="element-222">
                <Eye className="w-7 h-7 sm:w-8 sm:h-8 text-cyan-600" data-id="element-223" />
              </div>
              <h3 className="text-2xl sm:text-3xl font-black text-slate-900 mb-4" data-id="element-224">
                Our Vision
              </h3>
              <p className="text-base sm:text-lg text-slate-700 leading-relaxed" data-id="element-225">
                To be Africa's leading technology partner, recognized for
                delivering world-class digital transformation solutions that
                bridge the gap between innovation and practical business impact.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial Carousel */}
      <section className="py-12 sm:py-20 bg-slate-50" data-id="element-226">
        <div className="max-w-4xl mx-auto px-4 sm:px-6" data-id="element-227">
          <div className="text-center mb-12 sm:mb-16" data-id="element-228">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-slate-900 mb-4" data-id="element-229">
              What Our Clients Say
            </h2>
            <p className="text-lg sm:text-xl text-slate-600" data-id="element-230">
              Trusted by leading organizations across Africa
            </p>
          </div>
          <TestimonialCarousel testimonials={testimonials} data-id="element-231" />
        </div>
      </section>

      {/* Philosophy */}
      <section className="py-12 sm:py-20 bg-gradient-to-br from-blue-900 to-slate-900 text-white" data-id="element-232">
        <div className="max-w-7xl mx-auto px-4 sm:px-6" data-id="element-233">
          <div className="text-center mb-12 sm:mb-16" data-id="element-234">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-black mb-4" data-id="element-235">
              Our Philosophy
            </h2>
            <p className="text-lg sm:text-xl text-slate-300 max-w-3xl mx-auto" data-id="element-236">
              We believe in a product-led, AI-first approach to technology
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8" data-id="element-237">
            <PhilosophyCard icon={Zap} title="Product-Led Innovation" description="We build solutions that users love, focusing on exceptional experiences and measurable outcomes rather than just features." data-id="element-238" />
            <PhilosophyCard icon={TrendingUp} title="AI-Powered Excellence" description="We leverage artificial intelligence and machine learning to create smarter, more efficient solutions that scale with your business." data-id="element-239" />
            <PhilosophyCard icon={Users} title="Partnership Approach" description="We work as an extension of your team, understanding your challenges and co-creating solutions that drive real business value." data-id="element-240" />
          </div>
        </div>
      </section>
    </div>;
}
function AnimatedStatCard({
  value,
  suffix,
  label,
  color
}: {
  value: number;
  suffix: string;
  label: string;
  color: string;
}) {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    cyan: 'bg-cyan-50 text-cyan-600',
    indigo: 'bg-indigo-50 text-indigo-600',
    purple: 'bg-purple-50 text-purple-600'
  };
  return <div className="p-6 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 text-center" data-id="element-241">
      <div className={`text-3xl sm:text-4xl font-black mb-2 ${colorClasses[color as keyof typeof colorClasses].split(' ')[1]}`} data-id="element-242">
        <AnimatedCounter end={value} suffix={suffix} data-id="element-243" />
      </div>
      <div className="text-xs sm:text-sm font-bold text-slate-700" data-id="element-244">{label}</div>
    </div>;
}
function PhilosophyCard({
  icon: Icon,
  title,
  description
}: {
  icon: any;
  title: string;
  description: string;
}) {
  return <div className="p-6 sm:p-8 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl hover:bg-white/10 transition-all duration-300 hover:-translate-y-2" data-id="element-245">
      <div className="w-12 h-12 sm:w-14 sm:h-14 bg-blue-500/20 rounded-xl flex items-center justify-center mb-6" data-id="element-246">
        <Icon className="w-6 h-6 sm:w-7 sm:h-7 text-blue-400" data-id="element-247" />
      </div>
      <h3 className="text-lg sm:text-xl font-black mb-3" data-id="element-248">{title}</h3>
      <p className="text-slate-300 leading-relaxed text-sm sm:text-base" data-id="element-249">
        {description}
      </p>
    </div>;
}